import { AnalyticsService } from 'base/analytics';
import { capitalizeFirstLetter } from 'base/helpers';
import { LibraryCache } from 'base/library_cache';
import { generateReferralUrl } from 'base/monotype';
import { extractPaletteColor } from 'base/styleguide/color';
import { generateScheme } from 'base/styleguide/concept';
import { isValidFontWeight } from 'base/styleguide/fonts';
import {
  ColorVariable,
  CompiledChromaticColor,
  CompiledNeutralColor,
} from 'base/styleguide/types';
import { getUiStyleComponentSlug } from 'base/styleguide/ui_styles';
import {
  Button,
  ButtonPalette,
  Options,
  RadioButtonGroup,
} from 'base/ui/button';
import { Icon } from 'base/ui/icon';
import {
  IconAdd,
  IconChevronDown,
  IconDelete,
  IconDesktop,
  IconExpand,
  IconExternal,
  IconFit,
  IconMobile,
  IconModeDark,
  IconModeLight,
  IconShuffleAi,
  IconTextSize,
} from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { LottieButton } from 'base/ui/lottie';
import { MenuButton } from 'base/ui/menu';
import { menuDivider, menuItem, menuText } from 'base/ui/menu_model';
import { ToastQueue } from 'base/ui/toast';
import {
  TextMediumLight,
  TextRegular,
  TextSmall,
  TextTiny,
  Title5,
} from 'base/ui/typography';
import { useOnMount } from 'base/useOnMount';
import { BlocksService } from 'blocks/blocks_service';
import { FontMetaInfo } from 'blocks/editor/styleguide/FontMeta';
import { HomepagePreview } from 'blocks/editor/styleguide/homepage_preview';
import { useConceptStyleGuideVariables } from 'blocks/editor/styleguide/style_application/concept_styleguide';
import { shortcut, useKeyboardShortcuts } from 'blocks/keyboard_shortcuts';
import { isMac } from 'blocks/platform';
import { useResizeObserver } from 'blocks/useResizeObserver';
import classNames from 'classnames';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { CSSProperties, MouseEvent, useState } from 'react';
import { ProjectEditingActions } from '../editing_actions/project';
import { SelectionStore } from '../selection/store';
import { SelectionActions } from '../selection_actions';
import { ProjectSession } from '../session/session';
import cardStyleThumbnailImage from './card-style-thumbnail.file.webp';
import { CARD_STYLES, getCardStyleLabel } from './card_styles';
import { CardStylePreview } from './card_styles_panel';
import { ConceptAPI, getGlobalConceptsStore } from './concept_store';
import { globalRemoteFontStore } from './fonts';
import { prefetchPlaceholderBlurredImages } from './images';
import { Route, SidebarNavigation } from './routes';
import stickAnimationLottie from './stick_animation.lottie.json';
import styles from './styleguide.css';
import {
  globalTypeSettingsOverrideStore,
  HEADING_FONT_WEIGHTS,
  TYPE_SETTING_SIZE_LABELS,
  TYPE_SETTING_STYLE_LABELS,
  TYPE_SETTING_WEIGHT_LABELS,
} from './type_settings';
import {
  getIconForFontSource,
  LockButton,
  SegmentedFooter,
  ShadeSwatches,
} from './ui';
import { UI_STYLES } from './ui_styles';
import { UiStyleCardContent } from './ui_styles_panel';

const LockableCard = ({
  title,
  onClick,
  onShuffle,
  locked,
  onLock,
  children,
  className,
}: React.PropsWithChildren<{
  title: string;
  onClick?: () => void;
  onShuffle?: () => void;
  locked: boolean;
  onLock?: (locked: boolean) => void;
  className?: string;
}>) => {
  return (
    <div
      className={classNames(styles.lockableCard, className)}
      onClick={onClick}
    >
      <Inline spacing={0} xalign="space-between">
        <TextRegular color="black">{title} </TextRegular>
        <div className={styles.lockableCardActions}>
          <Inline spacing={3.2} yalign="center">
            <span className={styles.hidden}>
              <Button
                icon={IconShuffleAi}
                iconColor="ai"
                variant="subtle"
                tooltip="Shuffle"
                onClick={(e) => {
                  e.stopPropagation();
                  onShuffle?.();
                }}
                disabled={!onShuffle}
              />
            </span>
            <div
              className={classNames(styles.lockButton, locked && styles.locked)}
            >
              <LockButton locked={locked} onLockClick={onLock || (() => {})} />
            </div>
          </Inline>
        </div>
      </Inline>
      <div className={styles.lockableCardContent}>{children}</div>
    </div>
  );
};

const FontFamilyCard = observer(
  ({
    analytics,
    target,
    navigation,
  }: {
    analytics: AnalyticsService;
    target: 'heading' | 'body';
    navigation: SidebarNavigation;
  }) => {
    const conceptsStore = getGlobalConceptsStore();
    const concept = conceptsStore.activeConcept;
    const selectedFontFamily = concept.getFontFamily(target);

    const onClick = () => {
      navigation.goto({ path: 'fontFamily', target });
      analytics.track({
        event: 'project:typography_card_click',
        target,
      });
    };
    const viewFont = (e: React.MouseEvent) => {
      e.stopPropagation();
      analytics.track({
        event: 'project:typography_view_button_click',
        target,
      });
      if (!fontFamily?.acquisitionUrl) {
        return;
      }

      const url = generateReferralUrl(fontFamily.acquisitionUrl);
      analytics.track({
        event: 'visit_font_information_button_clicked',
        link: url,
      });
      window.open(url, '_blank noopener noreferrer');
    };
    const onLock = (locked: boolean) => {
      concept.lockFontFamily(target, locked);
      analytics.track({
        event: 'project:typography_lock_button_click',
        target,
      });
    };
    const onShuffle = async () => {
      await concept.shuffle({
        typography: { [target]: true },
        overrideLocked: true,
      });
      analytics.track({
        event: 'project:typography_shuffle_button_click',
        target,
      });
    };

    const fontFamily = conceptsStore.fontMetadataService.getFontFamily(
      selectedFontFamily.slug,
    )?.value;
    const fontWeights = concept.getFontWeights(target);

    if (
      globalRemoteFontStore.isRemoteFontFamilyLoading(
        selectedFontFamily.slug,
        fontWeights,
      ) ||
      (target === 'heading' && concept.isShufflingHeading) ||
      (target === 'body' && concept.isShufflingBody)
    ) {
      return <div className={styles.placeholderFontFamilyCard} />;
    }

    return (
      <LockableCard
        title={capitalizeFirstLetter(target)}
        onClick={onClick}
        onShuffle={onShuffle}
        locked={selectedFontFamily.locked}
        onLock={onLock}
        className={styles.fontFamilyCard}
      >
        {fontFamily &&
        globalRemoteFontStore.isRemoteFontFamilyLoaded(
          selectedFontFamily.slug,
          fontWeights,
        ) ? (
          <Rows spacing={8}>
            <div
              className={styles.fontFamilyCardName}
              style={{
                fontFamily: `'${selectedFontFamily.slug}'`,
                fontWeight: fontWeights[0],
              }}
            >
              <div>{fontFamily.name}</div>
            </div>
            <div className={styles.fontFamilyCardFooter}>
              <SegmentedFooter>
                <div className={styles.fontIconSourceContainer}>
                  <span className={styles.fontFamilySourceIcon}>
                    {getIconForFontSource(fontFamily.source)}
                  </span>
                  <TextTiny color="black">
                    {fontFamily.source === 'M' ? 'Monotype' : 'Google'}
                  </TextTiny>
                </div>
                <FontMetaInfo fontFamily={fontFamily} priceDisplayMode="full" />
              </SegmentedFooter>

              <span className={styles.fontFamilyAction}>
                <Button
                  variant="secondary"
                  size="small"
                  icon={IconExternal}
                  onClick={viewFont}
                  tooltip="View font family"
                />
              </span>
            </div>
          </Rows>
        ) : (
          <TextRegular color="red">
            Error loading font '{selectedFontFamily.slug}'
          </TextRegular>
        )}
      </LockableCard>
    );
  },
);

function TypographySection({
  analytics,
  navigation,
}: {
  analytics: AnalyticsService;
  navigation: SidebarNavigation;
}) {
  return (
    <div className={styles.lockableCards}>
      <FontFamilyCard
        analytics={analytics}
        navigation={navigation}
        target="heading"
      />
      <FontFamilyCard
        analytics={analytics}
        navigation={navigation}
        target="body"
      />
    </div>
  );
}

const TypeSettingsDropdown = observer(
  ({
    analytics,
    concept,
  }: {
    analytics: AnalyticsService;
    concept: ConceptAPI;
  }) => {
    const conceptsStore = getGlobalConceptsStore();
    return (
      <MenuButton
        icon={IconTextSize}
        iconRight={IconChevronDown}
        label={`${
          TYPE_SETTING_SIZE_LABELS[concept.state.typeSettings.size]
        } - ${TYPE_SETTING_WEIGHT_LABELS[
          concept.state.typeSettings.weight
        ].toLowerCase()}`}
        search="hidden"
        menu={[
          menuText({ label: 'Size', size: 'tiny' }),
          ...(['small', 'regular', 'large'] as const).map((size) =>
            menuItem({
              label: `${TYPE_SETTING_SIZE_LABELS[size]}${
                size === 'small' ? ' (default)' : ''
              }`,
              onMouseEnter: () => {
                globalTypeSettingsOverrideStore.typeSettingsOverrides = {
                  size,
                };
              },
              onMouseLeave: () => {
                globalTypeSettingsOverrideStore.typeSettingsOverrides = {};
              },
              onClick: () => {
                concept.updateTypeSettings({ size });
                analytics.track({
                  event: 'project:typography_size_update',
                  size,
                });
              },
              checked: concept.state.typeSettings.size === size,
              closeOnClick: false,
            }),
          ),
          menuDivider(),
          menuText({ label: 'Weight', size: 'tiny' }),
          ...(['regular', 'medium', 'bold'] as const).map((weight) => {
            const activeFontMetadata =
              conceptsStore.fontMetadataService.getFontFamily(
                concept.state.fontFamilies.heading.slug,
              )?.value;
            const disabled = activeFontMetadata
              ? !isValidFontWeight(
                  HEADING_FONT_WEIGHTS[weight],
                  activeFontMetadata,
                )
              : false;
            return menuItem({
              disabled,
              disabledReason: disabled
                ? 'Not available for this font'
                : undefined,
              label: TYPE_SETTING_WEIGHT_LABELS[weight],
              onMouseEnter: !disabled
                ? () => {
                    globalTypeSettingsOverrideStore.typeSettingsOverrides = {
                      weight,
                    };
                  }
                : undefined,
              onMouseLeave: () => {
                globalTypeSettingsOverrideStore.typeSettingsOverrides = {};
              },
              onClick: () => {
                concept.updateTypeSettings({ weight });
                analytics.track({
                  event: 'project:typography_weight_update',
                  weight,
                });
              },
              checked: concept.state.typeSettings.weight === weight,
              closeOnClick: false,
            });
          }),
          menuDivider(),
          menuText({ label: 'Style', size: 'tiny' }),
          ...(['default', 'spacious', 'uppercase'] as const).map((style) =>
            menuItem({
              label: TYPE_SETTING_STYLE_LABELS[style],
              onMouseEnter: () => {
                globalTypeSettingsOverrideStore.typeSettingsOverrides = {
                  style,
                };
              },
              onMouseLeave: () => {
                globalTypeSettingsOverrideStore.typeSettingsOverrides = {};
              },
              onClick: () => {
                concept.updateTypeSettings({ style });
                analytics.track({
                  event: 'project:typography_style_update',
                  style,
                });
              },
              checked: concept.state.typeSettings.style === style,
              closeOnClick: false,
            }),
          ),
        ]}
        onClose={() => {
          globalTypeSettingsOverrideStore.typeSettingsOverrides = {};
        }}
      />
    );
  },
);

function EmptyChromaticCard({
  analytics,
  loading = false,
}: {
  analytics: AnalyticsService;
  loading?: boolean;
}) {
  const activeConcept = getGlobalConceptsStore().activeConcept;
  const onClick = () => {
    activeConcept.addChromatic();
    analytics.track({
      event: 'project:add_color_button_click',
      palette: 'chromatic',
      chromatic: activeConcept.chromatics.length,
    });
  };
  if (loading) {
    return (
      <div
        className={classNames(
          styles.emptyCard,
          styles.colorCard,
          styles.loadingCard,
        )}
      />
    );
  }
  return (
    <div
      className={classNames(styles.emptyCard, styles.colorCard)}
      onClick={onClick}
    >
      <Icon icon={IconAdd} />
    </div>
  );
}

type ChromaticColorCardProps = {
  analytics: AnalyticsService;
  index: number;
  color: CompiledChromaticColor;
  toastQueue: ToastQueue;
  onClick?: (e: MouseEvent) => void;
  onDelete?: () => void;
  loading?: boolean;
};

function ChromaticColorCard({
  analytics,
  index,
  color,
  toastQueue,
  onDelete = () => {},
  onClick = () => {},
  loading = false,
}: ChromaticColorCardProps) {
  const concept = getGlobalConceptsStore().activeConcept;
  const onLockClick = (toLock: boolean) => {
    concept.updateChromaticLocked(index, toLock);
    analytics.track({
      event: 'project:lock_color_button_click',
      palette: 'chromatic',
      chromatic: index + 1,
    });
  };

  const dereference = (color: ColorVariable) =>
    extractPaletteColor(concept, color);

  const { text } = generateScheme(
    { palette: color.palette, shade: 4, chromatic: color.chromatic },
    concept,
  );

  const textColor = dereference(text);

  const isPrimary = index === 0;
  // Really we should do this in css via `color-mix` but there is a bug in
  // chrome that causes it to not repaint correctly.
  // https://issues.chromium.org/issues/354513209
  const v = textColor === '#FFFFFF' ? 255 : 0;
  const tagBgColor = `rgba(${v},${v},${v},0.1)`;

  if (loading) {
    return <div className={classNames(styles.colorCard, styles.loadingCard)} />;
  }

  return (
    <div
      className={styles.colorCard}
      onClick={onClick}
      style={{ backgroundColor: color.hex, color: textColor }}
    >
      <div className={styles.colorCardHeader}>
        <Inline spacing={4} xalign="space-between" yalign="center">
          <TextSmall>{color.name}</TextSmall>
          <Inline spacing={4} xalign="end">
            {!isPrimary && !color.locked && (
              <div className={styles.deleteButton}>
                <Button
                  icon={IconDelete}
                  variant="subtle"
                  tooltip="Delete"
                  onClick={(e) => {
                    e.preventDefault();
                    onDelete();
                  }}
                />
              </div>
            )}
            <div
              className={classNames(
                styles.lockButton,
                concept.state.chromatics[index].locked && styles.locked,
              )}
            >
              <LockButton locked={color.locked} onLockClick={onLockClick} />
            </div>
          </Inline>
        </Inline>
      </div>
      <div className={styles.colorCardFooter}>
        <Rows spacing={8}>
          <Inline xalign="space-between" yalign="center">
            <Inline spacing={8} xalign="start" yalign="center">
              <TextMediumLight>
                {color.hex.substring(1).toUpperCase()}
              </TextMediumLight>
            </Inline>
            {isPrimary && (
              <div
                className={styles.colorCardTag}
                style={{ background: tagBgColor }}
              >
                <TextTiny>Main</TextTiny>
              </div>
            )}
          </Inline>
          <ShadeSwatches
            color={color}
            textColor={textColor}
            toastQueue={toastQueue}
          />
        </Rows>
      </div>
    </div>
  );
}

function NeutralColorCard({
  analytics,
  neutral,
  onClick = () => {},
  loading = false,
}: {
  analytics: AnalyticsService;
  neutral: CompiledNeutralColor;
  onClick?: () => void;
  loading?: boolean;
}) {
  const onLockClick = (toLock: boolean) => {
    getGlobalConceptsStore().activeConcept.updateNeutralLocked(toLock);
    analytics.track({
      event: 'project:lock_color_button_click',
      palette: 'neutral',
    });
    return;
  };
  if (loading) {
    return (
      <div className={classNames(styles.neutralCard, styles.loadingCard)} />
    );
  }
  return (
    <div className={styles.neutralCard} onClick={onClick}>
      <Rows spacing={0} fixedHeight>
        <div className={classNames(styles.neutralCardHeader)}>
          <Inline xalign="space-between" yalign="center">
            <TextSmall color="inherit">Neutrals</TextSmall>
            <div
              className={classNames(
                styles.lockButton,
                neutral.locked && styles.locked,
              )}
            >
              <LockButton locked={neutral.locked} onLockClick={onLockClick} />
            </div>
          </Inline>
        </div>

        {neutral.shades.slice(1).map((shade, index) => (
          <div
            key={index}
            className={styles.neutralSwatch}
            style={{ background: shade }}
          ></div>
        ))}
      </Rows>
    </div>
  );
}

export const Colors = observer(function _Colors({
  analytics,
  concept,
  toastQueue,
  navigation,
}: {
  analytics: AnalyticsService;
  concept: ConceptAPI;
  toastQueue: ToastQueue;
  navigation: SidebarNavigation;
}) {
  const onDeleteChromatic = (index: number) => {
    concept.removeChromatic(index);
    analytics.track({
      event: 'project:delete_color_button_click',
      palette: 'chromatic',
      chromatic: index + 1,
    });
  };
  const chromatics = concept.chromatics;
  let layoutClass = `layout${chromatics.length}`;

  return (
    <div className={classNames(styles.allColors, styles[layoutClass])}>
      <div className={styles.themeColor}>
        <NeutralColorCard
          analytics={analytics}
          neutral={concept.neutral}
          onClick={() => {
            navigation.goto({ path: 'colors/neutral' });
            analytics.track({
              event: 'project:color_card_click',
              palette: 'neutral',
            });
          }}
          loading={concept.isShufflingColors && !concept.neutral.locked}
        />
      </div>
      <div className={styles.primaryColor}>
        <ChromaticColorCard
          analytics={analytics}
          color={chromatics[0]}
          index={0}
          toastQueue={toastQueue}
          onClick={(e) => {
            if (e.defaultPrevented) return;
            navigation.goto({ path: `colors/chromatic1` } as Route);
            analytics.track({
              event: 'project:color_card_click',
              palette: 'chromatic',
              chromatic: 1,
            });
          }}
          loading={concept.isShufflingColors && !concept.chromatics[0].locked}
        />
      </div>
      {chromatics.slice(1).map((color, index) => (
        <ChromaticColorCard
          analytics={analytics}
          key={index + 1}
          color={color}
          index={index + 1}
          toastQueue={toastQueue}
          onDelete={() => onDeleteChromatic(index + 1)}
          onClick={(e) => {
            if (e.defaultPrevented) return;
            navigation.goto({ path: `colors/chromatic${index + 2}` } as Route);
            analytics.track({
              event: 'project:color_card_click',
              palette: 'chromatic',
              chromatic: index + 2,
            });
          }}
          loading={concept.isShufflingColors && !color.locked}
        />
      ))}
      {chromatics.length < 5 ? (
        <EmptyChromaticCard
          analytics={analytics}
          loading={concept.isShufflingColors}
        />
      ) : (
        ''
      )}
    </div>
  );
});

export const StyleGuide = observer(function _Styleguide({
  navigation,
  selection,
  selectionActions,
  analytics,
  session,
  editing,
  library,
  toastQueue,
  blocksService,
}: {
  navigation: SidebarNavigation;
  selection: SelectionStore;
  selectionActions: SelectionActions;
  analytics: AnalyticsService;
  session: ProjectSession;
  editing: ProjectEditingActions | undefined;
  library: LibraryCache;
  toastQueue: ToastQueue;
  blocksService: BlocksService;
}) {
  const conceptsStore = getGlobalConceptsStore();
  const activeConcept = conceptsStore.activeConcept;
  const themeToggleOptions: Options<typeof activeConcept.state.theme> = [
    {
      value: 'light',
      label: '',
      icon: IconModeLight,
      tooltip: 'Light theme',
    },
    {
      value: 'dark',
      label: '',
      icon: IconModeDark,
      tooltip: 'Dark theme',
    },
  ];

  const setFullWidth = React.useCallback(
    (v: boolean) => {
      globalStyleGuideViewStore.fullWidth = v;
      navigation.close();
    },
    [navigation],
  );

  useKeyboardShortcuts(true, {
    [shortcut('c')]: () => {
      conceptsStore.activeConcept.shuffleColors();
      analytics.track({
        event: 'project:colors_shuffle_shortcut',
      });
    },
    [shortcut('i')]: () => {
      conceptsStore.activeConcept.shuffle({ images: true });
      analytics.track({
        event: 'project:image_shuffle_shortcut',
      });
    },
    [shortcut('t')]: () => {
      conceptsStore.activeConcept.shuffle({
        typography: { heading: true, body: true },
      });
      analytics.track({
        event: 'project:typography_shuffle_shortcut',
      });
    },
    [shortcut('u')]: () => {
      conceptsStore.activeConcept.shuffle({ uiStyle: true, cardStyle: true });
    },
    [shortcut(' ')]: () => {
      conceptsStore.activeConcept.shuffle({
        schemes: true,
      });
      analytics.track({
        event: 'project:page_shuffle_shortcut',
      });
    },
    [shortcut('\\', { cmd: true })]: () => {
      setFullWidth(!globalStyleGuideViewStore.fullWidth);
      analytics.track({
        event: 'project:full_width_preview_shortcut',
      });
    },
  });

  React.useEffect(() => {
    prefetchPlaceholderBlurredImages();
  }, []);

  // Prefetch UI style components.
  React.useEffect(() => {
    for (const cardStyle of CARD_STYLES) {
      for (const uiStyle of UI_STYLES) {
        library.getCachedPublicComponentJson(
          getUiStyleComponentSlug(uiStyle, cardStyle),
          {
            fetchIfMissing: true,
          },
        );
      }
    }
  }, [library, session.flags]);

  const [isCmdPressed, setIsCmdPressed] = useState(false);
  useOnMount(() => {
    const modifierKey = isMac ? 'Meta' : 'Control';

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === modifierKey) {
        setIsCmdPressed(true);
      }
    }

    function handleKeyUp(e: KeyboardEvent) {
      if (e.key === modifierKey) {
        setIsCmdPressed(false);
      }
    }

    function resetCmdPressed() {
      setIsCmdPressed(false);
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', resetCmdPressed);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', resetCmdPressed);
    };
  });

  const [previewInnerRef, setPreviewInnerRef] = useState<HTMLDivElement | null>(
    null,
  );
  const [previewWidth, setPreviewWidth] = useState<number | null>(null);

  useResizeObserver(previewInnerRef, (entry) =>
    setPreviewWidth(entry.contentRect.width),
  );

  const allChromaticsLocked = activeConcept.chromatics.every((c) => c.locked);

  const allFontsLocked =
    activeConcept.getFontFamily('heading').locked &&
    activeConcept.getFontFamily('body').locked;

  return (
    <div
      className={classNames(
        styles.wrapper,
        globalStyleGuideViewStore.fullWidth && styles.fullWidth,
      )}
    >
      <div className={styles.builder}>
        <main className={styles.main}>
          <Rows spacing={48}>
            <Rows spacing={16}>
              <Inline spacing={0} yalign="center" xalign="space-between">
                <Title5 color="black">Colors</Title5>
                <Inline spacing={8} yalign="center">
                  <ButtonPalette>
                    <RadioButtonGroup
                      options={themeToggleOptions}
                      value={activeConcept.state.theme}
                      onChange={(v) => {
                        activeConcept.updateTheme(v);
                        analytics.track({
                          event:
                            v === 'light'
                              ? 'project:light_theme_button_click'
                              : 'project:dark_theme_button_click',
                        });
                      }}
                    />
                  </ButtonPalette>
                  <Button
                    label="Shuffle"
                    variant="secondary"
                    loading={false}
                    icon={IconShuffleAi}
                    onClick={() => {
                      activeConcept.shuffleColors();
                      analytics.track({
                        event: 'project:shuffle_color_button_click',
                        location: 'concept_editor',
                      });
                    }}
                    iconColor="ai"
                    disabled={
                      allChromaticsLocked ||
                      conceptsStore.activeConcept.isShufflingColors
                    }
                    tooltip={
                      allChromaticsLocked
                        ? 'Unlock colors to shuffle'
                        : 'Shuffle new colors'
                    }
                    shortcut="C"
                  />
                </Inline>
              </Inline>
              <Colors
                analytics={analytics}
                concept={activeConcept}
                toastQueue={toastQueue}
                navigation={navigation}
              />
            </Rows>
            <Rows spacing={16}>
              <Inline spacing={0} yalign="center" xalign="space-between">
                <Title5 color="black">Typography</Title5>
                <Inline spacing={8} yalign="center">
                  <TypeSettingsDropdown
                    analytics={analytics}
                    concept={conceptsStore.activeConcept}
                  />
                  <Button
                    label="Shuffle"
                    variant="secondary"
                    loading={false}
                    icon={IconShuffleAi}
                    iconColor="ai"
                    onClick={() => {
                      conceptsStore.activeConcept.shuffle({
                        typography: { heading: true, body: true },
                      });
                      analytics.track({
                        event: 'project:typography_shuffle_button_click',
                      });
                    }}
                    disabled={
                      allFontsLocked ||
                      (conceptsStore.activeConcept.isShufflingHeading &&
                        conceptsStore.activeConcept.isShufflingBody)
                    }
                    tooltip={
                      allFontsLocked
                        ? 'Unlock fonts to shuffle'
                        : 'Shuffle font pairing'
                    }
                    shortcut="T"
                  />
                </Inline>
              </Inline>
              <TypographySection
                analytics={analytics}
                navigation={navigation}
              />
            </Rows>
            <Rows spacing={16}>
              <Inline spacing={0} yalign="center" xalign="space-between">
                <Inline spacing={8} yalign="center">
                  <Title5 color="black">UI Styling</Title5>
                </Inline>
                <Inline spacing={8} yalign="center">
                  <Button
                    label="Shuffle"
                    variant="secondary"
                    icon={IconShuffleAi}
                    iconColor="ai"
                    disabled={
                      conceptsStore.activeConcept.uiStyleLocked &&
                      conceptsStore.activeConcept.cardStyleLocked
                    }
                    shortcut="U"
                    tooltip={
                      conceptsStore.activeConcept.uiStyleLocked &&
                      conceptsStore.activeConcept.cardStyleLocked
                        ? 'Unlock UI styling to shuffle'
                        : 'Shuffle UI styling'
                    }
                    onClick={() => {
                      conceptsStore.activeConcept.shuffle({
                        uiStyle: true,
                        cardStyle: true,
                      });
                    }}
                  />
                </Inline>
              </Inline>
              <UiElementsSection
                navigation={navigation}
                concept={conceptsStore.activeConcept}
                library={library}
              />
            </Rows>
          </Rows>
        </main>
      </div>
      <div
        style={useConceptStyleGuideVariables(activeConcept)}
        className={styles.preview}
      >
        <div className={styles.previewInner} ref={setPreviewInnerRef}>
          <HomepagePreview
            navigation={navigation}
            analytics={analytics}
            session={session}
            selection={selection}
            selectionActions={selectionActions}
            library={library}
            breakpoint={globalStyleGuideViewStore.previewMode}
            width={
              globalStyleGuideViewStore.previewMode === 'desktop' &&
              globalStyleGuideViewStore.fullWidth
                ? previewWidth
                : null
            }
            concept={activeConcept}
            blocksService={blocksService}
            isCmdPressed={isCmdPressed}
            editing
          />
        </div>
        <div className={styles.previewToggle}>
          <ButtonPalette
            isFullHeight
            isDarker={globalStyleGuideViewStore.previewMode === 'mobile'}
          >
            <RadioButtonGroup<'mobile' | 'desktop'>
              options={[
                {
                  value: 'desktop',
                  icon: IconDesktop,
                  tooltip: 'Desktop',
                },
                {
                  value: 'mobile',
                  icon: IconMobile,
                  tooltip: 'Mobile',
                },
              ]}
              size="small"
              value={globalStyleGuideViewStore.previewMode}
              onChange={(value) =>
                (globalStyleGuideViewStore.previewMode = value)
              }
            />
            <div
              style={{
                width: '1px',
                height: '14px',
                margin: 'auto 7px',
                background: 'rgba(0, 0, 0, 0.08)',
              }}
            />
            <Button
              variant={
                globalStyleGuideViewStore.fullWidth ? 'secondary' : 'subtle'
              }
              icon={globalStyleGuideViewStore.fullWidth ? IconFit : IconExpand}
              onClick={() => setFullWidth(!globalStyleGuideViewStore.fullWidth)}
              size="small"
              tooltip={
                globalStyleGuideViewStore.fullWidth
                  ? 'Shrink preview'
                  : 'Expand preview'
              }
              tooltipShortcut="⌘\"
            />
          </ButtonPalette>
        </div>
        <LottieButton
          label="Scheme shuffle"
          variant="secondary"
          animationData={stickAnimationLottie}
          iconColor="ai"
          shortcut="SPACE"
          onClick={() => {
            activeConcept.shuffle({ schemes: true });
            analytics.track({
              event: 'project:scheme_shuffle_button_click',
            });
          }}
          tooltip="Reapply new schemes to sections"
          fullWidth
        />
      </div>
    </div>
  );
});

const UiElementsSection = observer(
  ({
    navigation,
    concept,
    library,
  }: {
    navigation: SidebarNavigation;
    concept: ConceptAPI;
    library: LibraryCache;
  }) => {
    return (
      <div className={styles.lockableCards}>
        <LockableCard
          title="Buttons & Forms"
          locked={concept.uiStyleLocked}
          onLock={(v) => concept.setUiStyleLocked(v)}
          onShuffle={() =>
            concept.shuffle({ uiStyle: true, overrideLocked: true })
          }
          onClick={() => navigation.goto({ path: 'uiStyles' })}
        >
          <div className={styles.uiStyleCard}>
            <UiStyleCardContent
              uiStyle={concept.uiStyle}
              library={library}
              concept={concept}
              interactive={false}
            />
          </div>
        </LockableCard>
        <LockableCard
          title="Cards & Images"
          locked={concept.cardStyleLocked}
          onLock={(v) => concept.setCardStyleLocked(v)}
          onShuffle={() =>
            concept.shuffle({ cardStyle: true, overrideLocked: true })
          }
          onClick={() => navigation.goto({ path: 'cardStyles' })}
        >
          <div
            className={styles.cardStyleContainer}
            style={
              {
                '--heading-font-family': concept.getFontFamily('heading').slug,
                '--body-font-family': concept.getFontFamily('body').slug,
              } as CSSProperties
            }
          >
            <div className={styles.cardStyleRow}>
              <CardStylePreview
                className={styles.cardStyleCard1}
                cardStyle={concept.cardStyle}
                radiusOption={concept.cardStyleRadiusOption}
                foregroundColor="grey"
              >
                <div
                  className={styles.cardStyleCardFullThumbnail}
                  style={{
                    backgroundImage: `url(${cardStyleThumbnailImage})`,
                  }}
                />
              </CardStylePreview>
              <CardStylePreview
                className={styles.cardStyleCard2}
                cardStyle={concept.cardStyle}
                radiusOption={concept.cardStyleRadiusOption}
                foregroundColor="grey"
              >
                <div
                  className={styles.cardStyleCardThumbnail}
                  style={{
                    backgroundImage: `url(${cardStyleThumbnailImage})`,
                  }}
                />
                <div className={styles.cardStyleCard2Content}>
                  <div className={styles.cardStyleCardHeading}>
                    {getCardStyleLabel(concept.cardStyle)} Card
                  </div>
                  <div className={styles.cardStyleCardBody}>
                    Pick a card style that matches your overall aesthetic
                  </div>
                </div>
              </CardStylePreview>
              <CardStylePreview
                className={styles.cardStyleCard3}
                cardStyle={concept.cardStyle}
                radiusOption={concept.cardStyleRadiusOption}
                foregroundColor="grey"
              >
                <div className={styles.cardStyleCard3Content}>
                  <div className={styles.cardStyleCardHeading}>
                    Medium length section heading goes here
                  </div>
                  <div className={styles.cardStyleCardBody}>
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                  </div>
                </div>
                <div className={styles.cardStyleCardButton}>Button</div>
              </CardStylePreview>
            </div>
          </div>
        </LockableCard>
      </div>
    );
  },
);

class StyleGuideViewStore {
  @observable
  fullWidth: boolean = false;

  @observable
  previewMode: 'mobile' | 'desktop' = 'desktop';

  constructor() {
    makeObservable(this);
  }
}

export const globalStyleGuideViewStore = new StyleGuideViewStore();
